import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Package, MapPin, Calendar, Globe, Home, Star, Search, ArrowRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Compute starting price from hotel_options (lowest 2-PAX CPAI across all options), or fall back to legacy fields
const getStartingPrice = (pkg) => {
  if (pkg.hotel_options && pkg.hotel_options.length > 0) {
    const prices = [];
    pkg.hotel_options.forEach(opt => {
      (opt.pax_rows || []).forEach(r => {
        const cpai = r.prices && r.prices.CPAI;
        if (cpai != null && cpai > 0) prices.push(Number(cpai));
      });
    });
    if (prices.length > 0) return Math.min(...prices);
  }
  return pkg.per_adult_price || pkg.price || 0;
};

const AgentPackages = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => { fetchPackages(); }, []);

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${API}/packages?status=active&audience=agents`, { headers: { Authorization: `Bearer ${token}` } });
      setPackages(res.data);
    } catch (error) { console.error("Failed to load packages:", error); } finally { setLoading(false); }
  };

  const filtered = packages.filter(p => {
    if (filterType !== "all" && p.trip_type !== filterType) return false;
    if (searchQuery && !p.title.toLowerCase().includes(searchQuery.toLowerCase()) && !p.destination.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const requestQuote = (pkg) => {
    navigate('/agent/request-quote', { state: { fromPackage: {
      destination: pkg.destination, trip_type: pkg.trip_type || 'domestic',
      duration_nights: pkg.duration_nights || parseInt(pkg.duration) || 1,
      package_name: pkg.title, hotel_category: pkg.category === 'luxury' ? '5-star' : pkg.category === 'premium' ? '4-star' : '3-star'
    }}});
  };

  return (
    <DashboardLayout type="agent">
      <div className="space-y-6" data-testid="agent-packages-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center"><Package className="w-5 h-5 text-emerald-600" /></div>
            Travel Packages
          </h1>
          <p className="text-sm text-gray-500 mt-1 ml-[52px]">Browse readymade packages and request quotes instantly</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search packages..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-white border-gray-200" data-testid="agent-search-packages" />
          </div>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {["all", "domestic", "international"].map(t => (
              <button key={t} onClick={() => setFilterType(t)} data-testid={`agent-filter-${t}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filterType === t ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
          <Badge className="bg-gray-100 text-gray-600">{filtered.length} packages</Badge>
        </div>

        {/* Package Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[1,2,3].map(i => <div key={i} className="h-72 bg-white rounded-xl animate-pulse border border-gray-100" />)}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="bg-white border-gray-200"><CardContent className="py-16 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-lg font-medium">No packages available</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="agent-packages-grid">
            {filtered.map(pkg => (
              <Card key={pkg.id} className="bg-white border-gray-200 shadow-sm overflow-hidden hover:shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer group" data-testid={`agent-pkg-${pkg.id}`}
                onClick={() => navigate(`/agent/packages/${pkg.id}`)}>
                <div className="h-44 bg-gradient-to-br from-emerald-100 to-teal-50 relative overflow-hidden">
                  {pkg.image_url ? (
                    <img src={pkg.image_url} alt={pkg.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><MapPin className="w-16 h-16 text-emerald-200" /></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                  <div className="absolute top-3 left-3 flex gap-1.5">
                    <Badge className={pkg.trip_type === 'international' ? 'bg-blue-600 text-white' : 'bg-emerald-600 text-white'}>
                      {pkg.trip_type === 'international' ? <Globe className="w-3 h-3 mr-1" /> : <Home className="w-3 h-3 mr-1" />}
                      {(pkg.trip_type || 'domestic').charAt(0).toUpperCase() + (pkg.trip_type || 'domestic').slice(1)}
                    </Badge>
                    {pkg.is_featured && <Badge className="bg-amber-500 text-white"><Star className="w-3 h-3" /></Badge>}
                  </div>
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="text-white font-bold text-lg leading-tight drop-shadow-md">{pkg.title}</h3>
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 text-xs text-gray-500 mb-3">
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{pkg.destination}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{pkg.duration_nights || parseInt(pkg.duration) || 0}N / {pkg.duration_days || (parseInt(pkg.duration) || 0) + 1}D</span>
                  </div>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-3">{pkg.description}</p>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xs text-gray-400">Starting from</span>
                      <p className="text-lg font-bold text-emerald-700">
                        {getStartingPrice(pkg) > 0 ? `INR ${getStartingPrice(pkg).toLocaleString()}` : 'On Request'}
                      </p>
                    </div>
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/agent/packages/${pkg.id}`); }} className="bg-emerald-600 hover:bg-emerald-700 gap-1 text-xs" data-testid={`view-pkg-${pkg.id}`}>
                      View <ArrowRight className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AgentPackages;
